import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { organisateurApi, PorteInfo, lireSession, ecrireSession } from '../../../lib/organisateurApi';
import { PagePublique, CartePublique, EtatFinal } from '../patron/PagePublique';
import { OrganisateurPorte } from './OrganisateurPorte';
import { OrganisateurPortail } from './OrganisateurPortail';

/**
 * /organisateur/:token — le Portail Organisateur (refonte design lot 10, maquette 19). Le lien seul ne
 * suffit pas : la porte (19a) demande le code à 6 chiffres reçu par courriel, qui ouvre une session de
 * 12 h ; le portail (19b) vient ensuite. Ce composant ne fait qu'orchestrer : quel écran, avec quelle
 * session, et quoi dire quand le lien ne mène nulle part (19e « Lien inconnu »).
 */
export function OrganisateurPage() {
    const { token = '' } = useParams<{ token: string }>();
    const [phase, setPhase] = React.useState<'chargement' | 'inconnu' | 'hors_service' | 'porte' | 'portail'>('chargement');
    const [porte, setPorte] = React.useState<PorteInfo | null>(null);
    const [avertissement, setAvertissement] = React.useState('');

    const charger = React.useCallback(async () => {
        const r = await organisateurApi.porte(token);
        if (r.status === 0) { setPhase('hors_service'); return; }
        if (r.status === 404 || r.data.etat === 'inconnu') { setPhase('inconnu'); return; }
        setPorte(r.data);
        // Une session encore valable (12 h) évite de ressaisir le code ; sinon on oublie le jeton périmé.
        if (r.data.session_ouverte && lireSession(token)) setPhase('portail');
        else { ecrireSession(token, ''); setPhase('porte'); }
    }, [token]);
    React.useEffect(() => { charger(); }, [charger]);

    const sessionPerdue = (message: string) => {
        ecrireSession(token, '');
        setAvertissement(message);
        setPhase('chargement');
        charger();
    };

    if (phase === 'chargement') return <div className="min-h-screen bg-surface-app flex items-center justify-center"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    if (phase === 'inconnu' || phase === 'hors_service') {
        return (
            <PagePublique largeur={440}>
                <CartePublique>
                    <div className="p-5">
                        {phase === 'inconnu'
                            ? <EtatFinal ton="danger" titre="Lien inconnu" texte="Ce lien ne correspond à rien. Vérifiez qu'il est complet — les courriels le coupent parfois en deux." action="Revenir au courriel reçu" />
                            : <EtatFinal ton="danger" titre="Hors service" texte="Le service est momentanément indisponible. Votre saisie n'est pas perdue." action={<button type="button" onClick={() => { setPhase('chargement'); charger(); }} className="underline underline-offset-2">Réessayer</button>} />}
                    </div>
                </CartePublique>
            </PagePublique>
        );
    }

    if (phase === 'porte' || !porte) {
        return <OrganisateurPorte token={token} porte={porte!} avertissement={avertissement}
            onOuvert={(session) => { ecrireSession(token, session); setAvertissement(''); setPhase('portail'); }} />;
    }

    return <OrganisateurPortail token={token} onSessionPerdue={sessionPerdue}
        onDeconnexion={async () => { await organisateurApi.deconnexion(token); ecrireSession(token, ''); setAvertissement(''); setPhase('chargement'); charger(); }} />;
}
